import { BaseEffects } from '@floori-web/utils/abstract';
import { createEffect, ofType } from '@ngrx/effects';
import { errorActions } from '@floori-web/store';
import { takeUntil, tap } from 'rxjs';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { FlooriTrackingEventsList } from '@floori-web/models';
import {
    FlooriDialogProvider,
    FLOORI_DIALOG_SERVICE,
    FlooriDialogRef,
    flooriHelperClasses,
    FlooriSnackbarComponent,
    ResponseTypes,
    SnackbarDialogData,
    SnackbarPosition,
} from '@floori-web-components';
import { FLOORI_TRACKING_PROVIDER } from '@floori-web/constants';
import { FlooriTrackingProvider } from '@floori-web/tracking';

@Injectable()
export class ErrorEffects extends BaseEffects implements OnDestroy {
    private readonly dialog = inject<FlooriDialogProvider>(FLOORI_DIALOG_SERVICE);
    private readonly trackingProvider = inject<FlooriTrackingProvider>(FLOORI_TRACKING_PROVIDER);
    private dialogRef?: FlooriDialogRef<FlooriSnackbarComponent, SnackbarDialogData>;

    private readonly setErrorWindow$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(errorActions.setError),
                tap(({ show, message }) => {
                    if (this.dialogRef && show) {
                        return;
                    }

                    if (!show) {
                        this.dialogRef?.close();
                        return;
                    }

                    this.showDialog(message);
                }),
            ),
        { dispatch: false },
    );

    private showDialog(message?: string): void {
        this.trackingProvider.track(FlooriTrackingEventsList.noConnection);
        this.dialogRef = this.dialog.open<FlooriSnackbarComponent, SnackbarDialogData>(
            FlooriSnackbarComponent,
            {
                containerClass: flooriHelperClasses['stretchWrapper'],
                panelClass: flooriHelperClasses['noClick'],
                hasBackdrop: false,
                closeOnDestroy: true,
                data: {
                    text: message || 'connectionError',
                    position: SnackbarPosition.topRight,
                    type: ResponseTypes.error,
                    showCloseButton: true,
                },
            },
            true, // Close after default duration (2s)
        ) as FlooriDialogRef<FlooriSnackbarComponent, SnackbarDialogData>;
        this.dialogRef
            .afterClosed()
            .pipe(
                takeUntil(this.destroyed$),
                tap(() => (this.dialogRef = undefined)),
            )
            .subscribe();
    }
}
